import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, FlaskConical } from "lucide-react";
import { Card, SectionHeader } from "../../components/lib";
import { Segmented, Field, ListRow } from "../../components/lib.overlay";
import { RND_PRODUCTS, FORMULAS, LAB_SESSIONS, SAMPLING_BATCHES, BLIND_PANELS, RND_DECISIONS, RND_TIMELINE } from "../../mocks/rndData";

export default function ProductWorkspace() {
  // Hook order must be stable; call every hook unconditionally, then
  // guard the empty state below so the hook sequence never changes
  // when the workspace fills up mid-session.
  const [productId, setProductId] = useState<string>(RND_PRODUCTS[0]?.id ?? "");
  const product = RND_PRODUCTS.find((p) => p.id === productId) ?? null;
  const formulas = useMemo(() => FORMULAS.filter((f) => f.productId === productId), [productId]);
  const labs     = useMemo(() => LAB_SESSIONS.filter((l) => l.products.includes(productId)), [productId]);
  const samples  = useMemo(() => SAMPLING_BATCHES.filter((s) => s.productId === productId), [productId]);
  const panels   = useMemo(() => BLIND_PANELS.filter((b) => b.productId === productId), [productId]);
  const decs     = useMemo(() => RND_DECISIONS.filter((d) => d.productId === productId), [productId]);
  const events   = useMemo(() => RND_TIMELINE.filter((t) => t.productId === productId), [productId]);

  // Empty-state guard — this legacy viewer dereferences `product.hero`,
  // `product.phase`, `product.codename`, etc. On an empty workspace that
  // throws before render, which GlobalErrorBoundary catches. Guard it.
  if (RND_PRODUCTS.length === 0 || !product) {
    return (
      <div className="space-y-6" data-testid="rd-workspace">
        <Card testId="rd-workspace-empty">
          <div className="py-10 px-4 text-center">
            <FlaskConical size={28} className="mx-auto opacity-40 mb-3" />
            <SectionHeader
              eyebrow="R&D Product Workspace"
              title="No R&D products in this workspace yet"
            />
            <div className="mt-2 text-sm max-w-md mx-auto" style={{ color: "var(--ph-ink-muted)" }}>
              This legacy per-product summary reads a product-anchored feed.
              Add your first product in the canonical R&amp;D Laboratory to
              populate it.
            </div>
            <div className="mt-4">
              <Link
                to="/pureh/rd-lab"
                data-testid="rd-workspace-open-rd-lab"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded"
                style={{ background: "var(--ph-brand-primary)", color: "#F7F3EB" }}
              >
                Open R&amp;D Laboratory
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rd-workspace">
      <Card testId="rd-ws-picker">
        <SectionHeader eyebrow="Workspace" title="Choose a product" />
        <Segmented<string>
          testId="rd-ws-seg"
          value={productId}
          onChange={setProductId}
          options={RND_PRODUCTS.map((p) => ({ value: p.id, label: p.name }))}
        />
      </Card>

      <Card testId="rd-ws-header">
        <div style={{ background: product.hero, height: 120, borderRadius: "var(--ph-radius-md)" }} className="mb-3" />
        <SectionHeader eyebrow={product.codename} title={product.name} action={<span className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>{product.family} · {product.phase}</span>} />
        <div className="text-sm" style={{ color: "var(--ph-ink-default)" }}>{product.brief}</div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card testId="rd-ws-formulas" padded={false}>
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}><SectionHeader eyebrow="Formulas" title={`${formulas.length} version${formulas.length === 1 ? "" : "s"}`} /></div>
          <div className="p-2">
            {formulas.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>No formulas yet.</div>}
            {formulas.map((f) => (
              <ListRow key={f.id} testId={`rd-ws-f-${f.id}`}
                label={<span>{f.version} <span className="text-[10px] font-mono ml-2" style={{ color: "var(--ph-ink-muted)" }}>{f.status}</span></span>}
                sublabel={<>{f.createdBy} · {f.createdRelative} · IFRA {f.ifraCompliant ? "OK" : "OVER"}</>}
                trailing={<span className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>EGP {(f.costEGPPerLiter/1000).toFixed(1)}K/L</span>}
              />
            ))}
          </div>
        </Card>

        <Card testId="rd-ws-labs" padded={false}>
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}><SectionHeader eyebrow="Lab" title={`${labs.length} session${labs.length === 1 ? "" : "s"}`} /></div>
          <div className="p-2">
            {labs.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>No sessions yet.</div>}
            {labs.map((l) => (
              <ListRow key={l.id} testId={`rd-ws-l-${l.id}`}
                label={<span>{l.code}</span>}
                sublabel={<>{l.perfumer} · {l.dateRelative} · {l.outcome}</>}
              />
            ))}
          </div>
        </Card>

        <Card testId="rd-ws-samples" padded={false}>
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}><SectionHeader eyebrow="Sampling" title={`${samples.length} batch${samples.length === 1 ? "" : "es"}`} /></div>
          <div className="p-2">
            {samples.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>No sampling yet.</div>}
            {samples.map((s) => (
              <ListRow key={s.id} testId={`rd-ws-s-${s.id}`}
                label={<span>{s.code} · {s.version}</span>}
                sublabel={<>{s.panel} · {s.units} u · {s.status} · returns {s.responsesIn}/{s.responsesTotal}</>}
              />
            ))}
          </div>
        </Card>

        <Card testId="rd-ws-panels" padded={false}>
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}><SectionHeader eyebrow="Blind" title={`${panels.length} panel${panels.length === 1 ? "" : "s"}`} /></div>
          <div className="p-2">
            {panels.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>No panel results.</div>}
            {panels.map((b) => (
              <ListRow key={b.id} testId={`rd-ws-b-${b.id}`}
                label={<span>Panel {b.panel} · {b.version}</span>}
                sublabel={<>{b.responsesIn}/{b.panelSize} responses · overall {b.overall.toFixed(1)}/10 · {b.recommendation}</>}
              />
            ))}
          </div>
        </Card>

        <Card testId="rd-ws-decs" padded={false}>
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}><SectionHeader eyebrow="Decisions" title={`${decs.length}`} /></div>
          <div className="p-2">
            {decs.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>No decisions logged.</div>}
            {decs.map((d) => (
              <ListRow key={d.id} testId={`rd-ws-d-${d.id}`}
                label={<span>{d.decision.toUpperCase()}</span>}
                sublabel={<>{d.decidedBy} · {d.dateRelative} · EGP {(d.budgetImpactEGP/1000).toFixed(0)}K</>}
              />
            ))}
          </div>
        </Card>

        <Card testId="rd-ws-timeline" padded={false}>
          <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}><SectionHeader eyebrow="Timeline" title={`${events.length} event${events.length === 1 ? "" : "s"}`} /></div>
          <div className="p-2">
            {events.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>No events.</div>}
            {events.map((e) => (
              <ListRow key={e.id} testId={`rd-ws-t-${e.id}`}
                label={<span>{e.title}</span>}
                sublabel={<>{e.kind} · {e.dateRelative}</>}
              />
            ))}
          </div>
        </Card>
      </div>

      <Card testId="rd-ws-ai">
        <div className="text-[10px] uppercase tracking-[0.14em] inline-flex items-center gap-1 mb-2" style={{ color: "var(--ph-ai)" }}><Sparkles size={10} /> Noor · AI Chief Perfumer</div>
        <div className="text-sm" style={{ color: "var(--ph-ink-default)" }}>
          {product.phase === "blind" ?
            "Split panel likely. Recommend reducing one dominant top or heart accord and re-testing with a smaller Panel A within 10 days." :
          product.phase === "formulation" ?
            "Iteration velocity is healthy. Keep IFRA computed live on each new dose change." :
          product.phase === "launched" ?
            "Signature scent is holding — recommend NOT reformulating within first 12 months of launch." :
            "Steady progress; keep the perfumer and the panel loop tight."}
        </div>
      </Card>
    </div>
  );
}
